/**
 * Drop-in replacement for wagmi's `useWriteContract`.
 *
 * Returns the identical shape — `{ writeContract, writeContractAsync, data,
 * isPending, isError, error, reset, status }` — so a screen swaps one line and
 * its JSX is untouched. That is the "no UI change" contract.
 *
 * What it adds: the journal row is written SYNCHRONOUSLY, before the wallet
 * prompt opens. That single ordering choice is what makes the stranded-withdraw
 * failure recoverable — today, a tab that dies between "user clicks confirm"
 * and "hash returns" leaves no trace anywhere that the operation was attempted.
 */

import { useCallback, useMemo, useRef, useSyncExternalStore } from "react";
import { useAccount, useChainId, usePublicClient, useWriteContract } from "wagmi";
import * as journal from "./journal";
import { entryKey } from "./keys";
import { EVENTS, isInFlight, isSettled, needsResetBeforeNewAttempt, toWriteShape } from "./machine";
import { useTxEngine } from "./TxEngineContext";
import { isChainMismatchError, useEnsureChain } from "../hooks/useEnsureChain";
import { toast } from "react-toastify";
import { isUserRejection, isRouterPaused, ROUTER_PAUSED_MESSAGE } from "./errors";
import { MAX_GAS_LIMIT } from "../utils/contractAddresses";

/**
 * Last line of defence on the gas limit.
 *
 * Infura rejects `eth_sendRawTransaction` above 16,777,216 gas outright. On
 * staging that is what stopped `cancelDispute` from ever being sent — the
 * wallet was handed a 41,000,000 limit and the RPC refused it, with an error
 * that reads like a contract revert ("the contract function cancelDispute
 * reverted") and sent everyone hunting for a bug in the contract.
 *
 * Clamping per call site was not enough: it only covered the screens someone
 * remembered to change. Every write in this app goes through this hook, so the
 * cap belongs here, where a screen cannot forget it.
 *
 * Only touches an explicit limit — a `variables` object with no `gas` is left
 * alone so the wallet still estimates for itself.
 */
function clampGas(variables) {
  if (!variables || variables.gas === undefined || variables.gas === null) {
    return variables;
  }
  try {
    // eslint-disable-next-line no-undef
    const requested = BigInt(variables.gas);
    if (requested <= MAX_GAS_LIMIT) return variables;
    console.warn(
      `[tx] gas limit ${requested} exceeds the ${MAX_GAS_LIMIT} cap — clamping. ` +
        `Sending it unclamped would be rejected by the RPC before it reached the chain.`
    );
    return { ...variables, gas: MAX_GAS_LIMIT };
  } catch (err) {
    return variables;
  }
}

/** Keep only what recovery and debugging need — never the ABI, which is huge. */
function serializeRequest(variables) {
  if (!variables || typeof variables !== "object") return null;
  return {
    to: variables.address || null,
    functionName: variables.functionName || null,
    args: variables.args || null,
    value: variables.value ?? null,
    gas: variables.gas ?? null,
  };
}

export function usePersistentWriteContract(options = {}) {
  const { flowId, stepId, instanceId, mutation, resumeSettled = false, ...wagmiOptions } =
    options;

  const { address } = useAccount();
  const chainId = useChainId();
  const publicClient = usePublicClient();
  const { accountType } = useTxEngine();
  const { ensureChain, isWrongChain, chainName } = useEnsureChain();

  const descriptor = useMemo(
    () => ({ chainId, address, flowId, stepId, instanceId, accountType }),
    [chainId, address, flowId, stepId, instanceId, accountType]
  );

  // Null until a wallet is connected — the hook then behaves like plain wagmi.
  const key = useMemo(
    () => (address && flowId && stepId ? entryKey(descriptor) : null),
    [address, flowId, stepId, descriptor]
  );

  const entry = useSyncExternalStore(
    journal.subscribe,
    // Referentially stable between real changes, so this cannot loop.
    useCallback(() => (key ? journal.getEntry(key) : null), [key]),
    useCallback(() => (key ? journal.getEntry(key) : null), [key])
  );

  /**
   * Was this row ALREADY finished when this component mounted?
   *
   * If so the component never witnessed the transition, so the outcome is
   * history, not news — and must not be projected. Reporting it anyway is what
   * reopened the "Funds are UnStaked successfully" modal on every navigation
   * back to the Stake page: that screen does
   * `if (isUnstakeConfirmed) setUnStakeSuccess(true)`, and on mount we handed it
   * a `true` left over from a transaction the user had already watched finish.
   *
   * Deliberately per-MOUNT, not per-page-load: in an SPA, navigating away and
   * back remounts without reloading, which is exactly the reported case.
   *
   * A row still IN FLIGHT at mount is not suppressed — that is the resume case
   * this engine exists for. Leave mid-transaction, come back, and the spinner is
   * restored and turns into success when the receipt lands.
   *
   * `resumeSettled` OPTS OUT of the suppression, per flow.
   *
   * Some steps exist precisely so a settled outcome can be recovered later. The
   * dispute and appeal fees are the case that forced this: the fee is taken by
   * the router, and the ONLY local trace that it was paid is this journal row.
   * Suppressing it at mount is what made the app ask for the fee a second time
   * after the user navigated away — and on the appeal path `openAppeal` has no
   * re-entry guard, so that second prompt really does take another 10 DAI.
   *
   * It is opt-in per call site and must stay that way. The default protects the
   * Stake screen, which does `if (isUnstakeConfirmed) setUnStakeSuccess(true)`
   * and would reopen its success modal on every navigation. Turning this on
   * globally would reintroduce that bug and re-fire post-confirmation effects
   * across the app.
   */
  const mountRef = useRef({ key: undefined, staleAtMount: false });
  if (mountRef.current.key !== key) {
    mountRef.current = {
      key,
      staleAtMount: !resumeSettled && !!(entry && isSettled(entry.status)),
    };
  }
  // Suppression lifts the moment the row goes back IN FLIGHT: from then on this
  // mount is watching the transaction itself, so its outcome is news again.
  // Without this the flag was one-shot per mount — a second unstake on a screen
  // you had navigated back to would confirm on chain and never report success,
  // leaving the modal and the refetch to never fire at all.
  if (mountRef.current.staleAtMount && entry && isInFlight(entry.status)) {
    mountRef.current.staleAtMount = false;
  }

  const wagmi = useWriteContract(wagmiOptions);

  // Latest-value refs so the callbacks below never need to be re-created (and
  // therefore never re-trigger a consumer's useEffect dependency array).
  const keyRef = useRef(key);
  keyRef.current = key;
  const descriptorRef = useRef(descriptor);
  descriptorRef.current = descriptor;
  const clientRef = useRef(publicClient);
  clientRef.current = publicClient;
  const mutationRef = useRef(mutation);
  mutationRef.current = mutation;

  /**
   * The wagmi handle lives in a REF, never in a dependency array.
   *
   * `useWriteContract()` returns a fresh object on every render. Putting it in
   * the deps below made our `writeContract` change identity every render — and
   * wagmi's own does NOT. Screens rely on that stability: CreateOrder.jsx lists
   * `burnToken` in a useEffect dep array, so an unstable identity re-ran the
   * effect on every render, which called `setBurnTransactionInitiated`, which
   * re-rendered... until React threw "Too many re-renders" and the whole Fund
   * screen died. Every callback this hook returns must be render-stable.
   */
  const wagmiRef = useRef(wagmi);
  wagmiRef.current = wagmi;

  /**
   * Fetch the nonce alongside the wallet prompt rather than before it. Fetching
   * first would insert an RPC round trip in front of every MetaMask popup —
   * a real, visible behaviour change. The row already exists by then, so this
   * only upgrades recovery from "ask the chain" to "look up the nonce".
   */
  const captureNonce = useCallback(async () => {
    const k = keyRef.current;
    const client = clientRef.current;
    const { address: addr } = descriptorRef.current;
    if (!k || !client || !addr) return;
    try {
      const nonce = await client.getTransactionCount({ address: addr, blockTag: "pending" });
      journal.apply(k, EVENTS.NONCE_CAPTURED, { nonce: Number(nonce) });
    } catch (err) {
      // Recovery still works via hash or the flow's on-chain verifier.
    }
  }, []);

  const beginStep = useCallback(
    (variables) => {
      const k = keyRef.current;
      if (!k) return;
      const existing = journal.ensureEntry(descriptorRef.current);

      // A new signing attempt SUPERSEDES whatever the last one left behind.
      //
      // Without this the row is stuck: `transition()` ignores SIGN_REQUESTED on
      // any settled row — terminal OR `confirmed` (that is what makes duplicate
      // receipts harmless), so
      // SIGN_REQUESTED would be a no-op and the hook would keep projecting the
      // PREVIOUS attempt's outcome forever. A second collateral post in the same
      // browser would come back `isSuccess: true` before the user had even
      // signed, firing the screen's confirmation handler against a stale row —
      // which is exactly the stuck "Post Collateral" spinner.
      //
      // `effects_pending` is reset too. Its effect belongs to a transaction that
      // already confirmed, and leaving the row would show stale success for the
      // NEW attempt. Dropping the backstop is safe here because the screen's own
      // direct call already delivered it on the happy path, and every effect we
      // register is idempotent server-side anyway.
      if (existing && needsResetBeforeNewAttempt(existing.status)) {
        journal.apply(k, EVENTS.RESET, {});
      }

      journal.apply(k, EVENTS.SIGN_REQUESTED, { request: serializeRequest(variables) });
      captureNonce();
    },
    [captureNonce]
  );

  const wrapCallbacks = useCallback(
    (callerOptions) => ({
      ...callerOptions,
      onSuccess: (hash, variables, context) => {
        const k = keyRef.current;
        if (k) journal.apply(k, EVENTS.HASH_RECEIVED, { hash });
        mutationRef.current?.onSuccess?.(hash, variables, context);
        callerOptions?.onSuccess?.(hash, variables, context);
      },
      onError: (error, variables, context) => {
        const k = keyRef.current;
        if (k) {
          journal.apply(
            k,
            isUserRejection(error) ? EVENTS.USER_REJECTED : EVENTS.SEND_FAILED,
            { error }
          );
        }
        // Every wallet write goes through here, so this is where a paused
        // router is named — once, whichever screen the user is on.
        if (isRouterPaused(error)) {
          toast.error(ROUTER_PAUSED_MESSAGE, { toastId: "router-paused" });
        }
        mutationRef.current?.onError?.(error, variables, context);
        callerOptions?.onError?.(error, variables, context);
      },
      onSettled: (data, error, variables, context) => {
        mutationRef.current?.onSettled?.(data, error, variables, context);
        callerOptions?.onSettled?.(data, error, variables, context);
      },
    }),
    []
  );

  /**
   * Every write in the app routes through this hook, so this is the one place
   * that has to hold the line on the chain.
   *
   * wagmi throws `ConnectorChainMismatchError` from inside the wallet prompt
   * when the connector and the connection disagree about the chain — an error
   * that names neither chain and, on AppKit's unsupported-network path, prints
   * the SAME id twice. Switching first turns that into either a working
   * transaction or a sentence the user can act on.
   *
   * The check is SYNCHRONOUS on the happy path. `isWrongChain` is already in
   * React state, so a correctly-connected wallet reaches `beginStep` and the
   * prompt with no await in front of either — the ordering the whole tx engine
   * is built on (see this file's header) is untouched. Only a wallet that is
   * actually on the wrong chain — which today throws anyway — takes the async
   * route, and it journals nothing until the switch has landed: a refused switch
   * is not an attempted transaction, and a row for one would leave recovery
   * chasing a transaction that was never signed.
   */
  const ensureChainRef = useRef(ensureChain);
  ensureChainRef.current = ensureChain;
  const chainErrorMessage = `Please switch your wallet to ${chainName} and try again`;

  const writeContract = useCallback(
    (rawVariables, callerOptions) => {
      const variables = clampGas(rawVariables);
      if (!isWrongChain) {
        beginStep(variables);
        return wagmiRef.current.writeContract(variables, wrapCallbacks(callerOptions));
      }

      return ensureChainRef.current().then((switched) => {
        if (switched) {
          beginStep(variables);
          return wagmiRef.current.writeContract(variables, wrapCallbacks(callerOptions));
        }
        // Mirror wagmi's callback contract rather than throwing: callers of the
        // non-async form only ever hear back through these, and their spinners
        // stay up forever otherwise.
        const error = new Error(chainErrorMessage);
        mutationRef.current?.onError?.(error, variables, undefined);
        callerOptions?.onError?.(error, variables, undefined);
        mutationRef.current?.onSettled?.(undefined, error, variables, undefined);
        callerOptions?.onSettled?.(undefined, error, variables, undefined);
        return undefined;
      });
    },
    [isWrongChain, chainErrorMessage, beginStep, wrapCallbacks]
  );

  const writeContractAsync = useCallback(
    async (rawVariables, callerOptions) => {
      const variables = clampGas(rawVariables);
      if (isWrongChain && !(await ensureChainRef.current())) {
        throw new Error(chainErrorMessage);
      }
      beginStep(variables);
      try {
        return await wagmiRef.current.writeContractAsync(variables, wrapCallbacks(callerOptions));
      } catch (error) {
        // `isWrongChain` reads React state, which can lag the connector by a
        // tick — the very drift this guard exists for. Repair it now so the
        // banner is accurate and the user's next click goes through, but do NOT
        // silently re-prompt: the journal row for this attempt is already closed
        // out as failed, and re-entering beginStep on it would corrupt its state.
        if (isChainMismatchError(error)) ensureChainRef.current();
        throw error;
      }
    },
    [isWrongChain, chainErrorMessage, beginStep, wrapCallbacks]
  );

  /**
   * Declare the post-confirmation effect for this step.
   *
   * Call it right after `writeContract`, while the form state that builds the
   * payload still exists. The effect stays inert until the transaction actually
   * confirms — a revert or a rejection never fires it — and from then on the
   * outbox owns delivery, so a reload cannot lose it.
   */
  const declareEffect = useCallback((type, payload) => {
    const k = keyRef.current;
    if (!k) return;
    journal.apply(k, EVENTS.EFFECT_INTENT, { type, payload });
  }, []);

  const reset = useCallback(() => {
    const k = keyRef.current;
    if (k) journal.apply(k, EVENTS.RESET, {});
    wagmiRef.current.reset?.();
  }, []);

  // Without a journal row (no wallet yet) behave exactly as plain wagmi does.
  const projected =
    key && entry && !mountRef.current.staleAtMount ? toWriteShape(entry) : null;

  return {
    ...wagmi,
    writeContract,
    writeContractAsync,
    declareEffect,
    reset,
    data: projected ? projected.data : wagmi.data,
    isPending: projected ? projected.isPending : wagmi.isPending,
    isError: projected ? projected.isError : wagmi.isError,
    error: projected ? projected.error || wagmi.error : wagmi.error,
    /** escape hatch for debugging / the engine itself */
    txEntry: entry,
  };
}

export default usePersistentWriteContract;
